/**
 * Map machine-readable IAM `reason` codes (set by the use-cases'
 * `Iam{Authorization,Validation,…}Error` and propagated through tRPC
 * via `error.data.iamReason`) to user-friendly Alert content.
 *
 * Why this lives here
 * -------------------
 * The backend message (e.g. "Roles ['superadmin'] can only be granted
 * by a founder.") is precise but written for a developer audience.
 * The IAM panel needs:
 *   1. A short title ("Insufficient permissions").
 *   2. A clear description in operator-language.
 *   3. A concrete suggested action ("Ask a founder to make this change.").
 *
 * Returning a structured object (instead of a single string) lets the
 * panel render an Alert with a TitleCase title + description + a
 * subdued "next step" line, all tone-matched to the destructive
 * variant. Falling back to the raw backend message for unknown
 * reasons keeps us forward-compatible with new use-cases that
 * haven't been added to the map yet.
 *
 * Drift policy
 * ------------
 * If you add a new IAM reason in the backend (any subclass of
 * `IamError` with a fresh `reason` literal) AND want a custom
 * frontend message for it, add a case here. Otherwise the operator
 * will see the raw backend message — which is fine, just a little
 * less polished.
 */
export interface IamFriendlyError {
    /** Short banner title — e.g. "Insufficient permissions". */
    title: string;
    /** Operator-readable description of WHY the action was refused. */
    description: string;
    /**
     * Optional concrete next step ("Ask a founder", "Switch back to
     * your partner", …). Rendered as a subdued line under the
     * description. `null` = no suggestion (rare).
     */
    suggestion: string | null;
    /** Display variant for the Alert component. */
    variant: 'destructive' | 'warning' | 'info';
}

/**
 * Map a tRPC error (post-`error.data.iamReason` extraction by the
 * CRM tRPC errorFormatter) to friendly content. `rawMessage` is the
 * raw backend message — used as the fallback `description` when the
 * reason isn't in the lookup map (or is null entirely, for non-IAM
 * errors like a network failure).
 */
export function iamReasonToFriendly(
    iamReason: string | null,
    rawMessage: string,
): IamFriendlyError {
    switch (iamReason) {
        case 'cannot_self_promote':
            return {
                title: 'You can\u2019t edit your own roles',
                description:
                    'For safety, no one can change their own tgdRoles or partner scope — even a founder. This stops a compromised session from quietly bumping itself.',
                suggestion:
                    'Ask another founder, superadmin, or payment_ops to perform this change.',
                variant: 'warning',
            };

        case 'only_founder_can_grant_privileged_role':
            return {
                title: 'Only a founder can grant this',
                description:
                    'Granting (or revoking) the superadmin and founder roles is reserved to the founder role \u2014 the root of trust for the IAM panel. Plain superadmins cannot mint other superadmins.',
                suggestion:
                    'Ask any user with the founder role to flip this checkbox. You can spot them in this directory by the founder badge.',
                variant: 'destructive',
            };

        case 'payment_ops_cannot_grant_superadmin':
            // Legacy reason kept for back-compat — the founder gate
            // catches this case first now, but a stale deploy or the
            // public REST adapter could still surface it.
            return {
                title: 'Only a founder can grant superadmin',
                description:
                    'Payment Ops can manage internal staff and partner staff, but minting another superadmin requires the founder role.',
                suggestion: 'Ask a founder to flip this checkbox.',
                variant: 'destructive',
            };

        case 'partneradmin_role_not_assignable':
            return {
                title: 'That role isn\u2019t assignable from a partner panel',
                description:
                    'Partneradmins can only assign roles within their own partner staff (accountmanager, partneradmin). Privileged Tagada-internal roles (payment_ops, tagadapaymentAdmin, superadmin, founder) require an internal hub or founder caller.',
                suggestion:
                    'Pick a role from the partner-assignable set, or ask a payment_ops/founder for the privileged ones.',
                variant: 'destructive',
            };

        case 'partneradmin_rx_option_required':
            return {
                title: 'Rx is not enabled for your partner',
                description:
                    'Rx roles (rx_admin, rx_ops) can only be handed out by a partner admin who holds rx_admin — that is how the Rx option is switched on.',
                suggestion: 'Ask Tagada to enable Rx for your partner (grant rx_admin to the partner owner).',
                variant: 'destructive',
            };

        case 'partneradmin_cross_scope_forbidden':
            return {
                title: 'Out-of-scope target',
                description:
                    'Partneradmins can only manage users attached to their own partner. The selected user belongs to a different partner.',
                suggestion:
                    'Ask the target partner\u2019s own admin (or a Tagada payment_ops) to make this change.',
                variant: 'destructive',
            };

        case 'scope_change_requires_superadmin':
            return {
                title: 'Re-homing a user is superadmin-only',
                description:
                    'Moving a user from one partner to another (or to/from internal staff) is a privileged operation — only superadmins can do it.',
                suggestion:
                    'Keep the partner scope unchanged in this edit, or ask a superadmin to re-home the user first.',
                variant: 'destructive',
            };

        case 'role_not_in_valid_set':
            return {
                title: 'Unknown role',
                description: rawMessage,
                suggestion:
                    'Pick a role from the checkbox list. If the legacy variant is real and we\u2019re missing it, ping engineering — the backend\u2019s VALID_TGD_ROLES is the source of truth.',
                variant: 'warning',
            };

        case 'not_authorized':
            return {
                title: 'Insufficient permissions',
                description:
                    'Your current tgdRoles do not allow this action.',
                suggestion:
                    'Ask a payment_ops or founder to perform this change, or to grant you the role you need.',
                variant: 'destructive',
            };

        case 'validation_failed':
            return {
                title: 'Invalid input',
                description: rawMessage,
                suggestion: null,
                variant: 'warning',
            };

        case 'not_found':
            return {
                title: 'User not found',
                description: rawMessage,
                suggestion:
                    'Double-check the email or Clerk user id, then retry. The Clerk webhook may also be lagging.',
                variant: 'warning',
            };

        // ─────────────────────────────────────────────────────────
        // Fallback: use the raw backend message but keep the same
        // structure so the UI doesn't need a separate code path.
        // ─────────────────────────────────────────────────────────
        default:
            return {
                title: iamReason
                    ? 'Action refused'
                    : 'Something went wrong',
                description: rawMessage,
                suggestion: null,
                variant: iamReason ? 'destructive' : 'destructive',
            };
    }
}

/**
 * Extract `iamReason` from any IAM-shaped error. Three lookup paths,
 * in priority order:
 *
 *   1. `err.data.iamReason`  → tRPC client error from the CRM
 *      `errorFormatter`. This is the canonical channel — every
 *      backend IAM denial lands here with a stable code.
 *
 *   2. `err.cause.iamReason` → tRPC v10 sometimes nests this under
 *      `cause.data` for batched errors; fall back gracefully so we
 *      don't lose the reason for a wrapped error.
 *
 *   3. `err.reason` (when `err.name?.startsWith('Iam')`) → for
 *      synthetic IAM-shaped objects we throw client-side to render
 *      the same Alert as a server-side denial would (e.g. the
 *      pre-flight founder gate in `toggleRole`). Same shape as the
 *      backend duck-type extractor in `api/crm/trpc/trpc.ts`.
 */
export function extractIamReason(err: unknown): string | null {
    if (!err || typeof err !== 'object') return null;
    const e = err as {
        name?: unknown;
        reason?: unknown;
        data?: { iamReason?: unknown } | null;
        cause?: { iamReason?: unknown } | null;
    };
    const fromData = e.data?.iamReason;
    if (typeof fromData === 'string' && fromData.length > 0) return fromData;
    const fromCause = e.cause?.iamReason;
    if (typeof fromCause === 'string' && fromCause.length > 0) return fromCause;
    if (
        typeof e.name === 'string' &&
        e.name.startsWith('Iam') &&
        typeof e.reason === 'string' &&
        e.reason.length > 0
    ) {
        return e.reason;
    }
    return null;
}

/**
 * Convenience: take any unknown error from a mutation and produce
 * the friendly Alert content in one call. The IAM panel uses this
 * everywhere instead of a bare `toast.error(err.message)`.
 */
export function toIamFriendlyError(err: unknown): IamFriendlyError {
    const reason = extractIamReason(err);
    const message =
        err instanceof Error
            ? err.message
            : typeof err === 'string'
                ? err
                : (err as { message?: unknown })?.message &&
                    typeof (err as { message?: unknown }).message === 'string'
                    ? ((err as { message: string }).message)
                    : 'Unexpected error';
    return iamReasonToFriendly(reason, message);
}
